import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { usePlatformAdmin } from '@/hooks/use-platform-admin';
import { 
  Shield, 
  Globe,
  Play,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Terminal,
  X,
  ExternalLink,
  Flag,
  SkipForward,
  Database,
  Layers
} from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';

const SCRAPER_SERVICE_URL = 'http://localhost:7077';

// Predefined category URLs to scrape
const CATEGORY_URLS = [
  'https://mars.arcelik.com.tr/shop/category/Bula%C5%9F%C4%B1k%20Makinesi.html/ref=c:2563',
  'https://mars.arcelik.com.tr/shop/category/Aspirat%C3%B6r%20-%20Davlumbaz.html/ref=c:2577',
  'https://mars.arcelik.com.tr/shop/category/F%C4%B1r%C4%B1n.html/ref=c:2583',
  'https://mars.arcelik.com.tr/shop/category/%C3%87ama%C5%9F%C4%B1r%20Makinesi.html/ref=c:2586',
  'https://mars.arcelik.com.tr/shop/category/So%C4%9Futucu.html/ref=c:2601',
  'https://mars.arcelik.com.tr/shop/category/Dondurucu.html/ref=c:2603',
  'https://mars.arcelik.com.tr/shop/category/Ocak.html/ref=c:2607',
  'https://mars.arcelik.com.tr/shop/category/%C3%87ama%C5%9F%C4%B1r%20Kurutma%20Makinesi.html/ref=c:2609',
  'https://mars.arcelik.com.tr/shop/category/Mikro%20Dalga%20F%C4%B1r%C4%B1n.html/ref=c:2610',
  'https://mars.arcelik.com.tr/shop/category/Su%20%C3%87%C3%B6z%C3%BCmleri.html/ref=c:2569',
  'https://mars.arcelik.com.tr/shop/category/Is%C4%B1t%C4%B1c%C4%B1lar.html/ref=c:2587',
  'https://mars.arcelik.com.tr/shop/category/Havaland%C4%B1rma.html/ref=c:2594',
  'https://mars.arcelik.com.tr/shop/category/Klima.html/ref=c:2597',
  'https://mars.arcelik.com.tr/shop/category/Hijyen.html/ref=c:2567',
  'https://mars.arcelik.com.tr/shop/category/Ki%C5%9Fisel%20Bak%C4%B1m.html/ref=c:2573',
  'https://mars.arcelik.com.tr/shop/category/S%C3%BCp%C3%BCrge.html/ref=c:2576',
  'https://mars.arcelik.com.tr/shop/category/Di%C4%9Fer%20KEA.html/ref=c:2578',
  'https://mars.arcelik.com.tr/shop/category/Mutfak%20Aletleri.html/ref=c:2593',
  'https://mars.arcelik.com.tr/shop/category/%C3%9Ct%C3%BC.html/ref=c:2611',
  'https://mars.arcelik.com.tr/shop/category/Yazarkasa.html/ref=c:2565',
  'https://mars.arcelik.com.tr/shop/category/Ses,%20Video%20ve%20G%C3%B6r%C3%BCnt%C3%BC%20Sistemleri.html/ref=c:2568',
  'https://mars.arcelik.com.tr/shop/category/Bilgisayar.html/ref=c:2581',
  'https://mars.arcelik.com.tr/shop/category/Cep%20Telefonu.html/ref=c:2585',
  'https://mars.arcelik.com.tr/shop/category/Televizyon.html/ref=c:2596',
  'https://mars.arcelik.com.tr/shop/category/Di%C4%9Fer%20T%C3%BCketici%20Elektroni%C4%9Fi.html/ref=c:2599',
  // Add more category URLs here as needed
];

interface ScraperSession {
  active: boolean;
  startedAt: string | null;
  categoriesScraped: number;
  totalProductsScraped: number;
  totalProductsInserted: number;
  totalSkippedToday: number;
}

interface ScraperStatus {
  browserOpen: boolean;
  loggedInHeuristic: boolean;
  lastRun: string | null;
  lastRunResult: {
    ok: boolean;
    totalExtracted?: number;
    validProducts?: number;
    newProductsInserted?: number;
    skippedAlreadyToday?: number;
    errorsCount?: number;
    clicksToLoadAll?: number;
    durationSeconds?: number;
    error?: string;
    session?: ScraperSession;
  } | null;
  logs: Array<{
    timestamp: string;
    level: string;
    message: string;
  }>;
  session: ScraperSession;
  config: {
    marsCategoryUrl: string;
    supabaseConfigured: boolean;
  };
}

const AdminMarsScraper = () => {
  const { isPlatformAdmin, loading: adminLoading } = usePlatformAdmin();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState<ScraperStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [openingBrowser, setOpeningBrowser] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [closingBrowser, setClosingBrowser] = useState(false);
  const [finishingSession, setFinishingSession] = useState(false);
  const [serviceError, setServiceError] = useState<string | null>(null);
  const [scrapingProgress, setScrapingProgress] = useState<{
    currentIndex: number;
    total: number;
    currentUrl: string;
  } | null>(null);

  // Fetch status from scraper service
  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch(`${SCRAPER_SERVICE_URL}/status`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setStatus(data);
      setServiceError(null);
    } catch (error: any) {
      setServiceError(
        error.message === 'Failed to fetch' 
          ? 'Scraper service is not running. Please start it with: cd scraper-service && npm run dev'
          : `Service error: ${error.message}`
      );
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll status every 2 seconds
  useEffect(() => {
    if (!adminLoading && !isPlatformAdmin) {
      toast({
        title: "Erişim Reddedildi",
        description: "Bu sayfaya erişim yetkiniz bulunmamaktadır.",
        variant: "destructive"
      });
      navigate('/');
      return;
    }

    if (isPlatformAdmin) {
      fetchStatus();
      const interval = setInterval(fetchStatus, 2000);
      return () => clearInterval(interval);
    }
  }, [isPlatformAdmin, adminLoading, navigate, toast, fetchStatus]);

  // Open browser handler
  const handleOpenBrowser = async () => {
    setOpeningBrowser(true);
    try {
      const response = await fetch(`${SCRAPER_SERVICE_URL}/open`, {
        method: 'POST',
      });
      const data = await response.json();
      
      if (data.ok) {
        toast({
          title: "Tarayıcı Açıldı",
          description: "Lütfen Mars portalında giriş yapın ve 2FA doğrulamasını tamamlayın.",
        });
      } else {
        throw new Error(data.error || 'Failed to open browser');
      }
    } catch (error: any) {
      toast({
        title: "Hata",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setOpeningBrowser(false);
      fetchStatus();
    }
  };

  // Start scrape handler - now scrapes all predefined URLs
  const handleStartScrape = async () => {
    setScraping(true);
    setScrapingProgress({ currentIndex: 0, total: CATEGORY_URLS.length, currentUrl: '' });
    
    let totalInserted = 0;
    let totalSkipped = 0;
    let successCount = 0;
    let failCount = 0;
    
    try {
      for (let i = 0; i < CATEGORY_URLS.length; i++) {
        const url = CATEGORY_URLS[i];
        setScrapingProgress({ currentIndex: i + 1, total: CATEGORY_URLS.length, currentUrl: url });
        
        toast({
          title: "Kategori Kazınıyor",
          description: `${i + 1}/${CATEGORY_URLS.length}: ${url.split('/').pop()?.split('.')[0]}`,
        });
        
        try {
          const response = await fetch(`${SCRAPER_SERVICE_URL}/scrape`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ url }),
          });
          const data = await response.json();
          
          if (data.ok) {
            totalInserted += data.newProductsInserted || 0;
            totalSkipped += data.skippedAlreadyToday || 0;
            successCount++;
          } else {
            failCount++;
            toast({
              title: `Kategori ${i + 1} Hatası`,
              description: data.error || 'Kazıma başarısız',
              variant: "destructive"
            });
          }
        } catch (error: any) {
          failCount++;
          toast({
            title: `Kategori ${i + 1} Hatası`,
            description: error.message,
            variant: "destructive"
          });
        }
        
        // Small delay between categories
        if (i < CATEGORY_URLS.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
      
      // Final summary
      toast({
        title: "Tüm Kategoriler Kazıma Tamamlandı",
        description: `${successCount} başarılı, ${failCount} hatalı. Toplam ${totalInserted} yeni ürün eklendi, ${totalSkipped} ürün zaten bugün kayıtlı (atlandı).`,
      });
      
    } catch (error: any) {
      toast({
        title: "Kazıma Hatası",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setScraping(false);
      setScrapingProgress(null);
      fetchStatus();
    }
  };

  // Finish session handler
  const handleFinishSession = async () => {
    setFinishingSession(true);
    try {
      const response = await fetch(`${SCRAPER_SERVICE_URL}/finish`, {
        method: 'POST',
      });
      const data = await response.json();
      
      if (data.ok) {
        const summary = data.sessionSummary;
        toast({
          title: "Oturum Tamamlandı",
          description: `${summary?.categoriesScraped || 0} kategori kazındı, ${summary?.totalProductsInserted || 0} yeni ürün eklendi.`,
        });
      } else {
        throw new Error(data.error || 'Failed to finish session');
      }
    } catch (error: any) {
      toast({
        title: "Hata",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setFinishingSession(false);
      fetchStatus();
    }
  };

  // Close browser handler
  const handleCloseBrowser = async () => {
    setClosingBrowser(true);
    try {
      const response = await fetch(`${SCRAPER_SERVICE_URL}/close`, {
        method: 'POST',
      });
      const data = await response.json();
      
      if (data.ok) {
        toast({
          title: "Tarayıcı Kapatıldı",
          description: "Tarayıcı başarıyla kapatıldı.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Hata",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setClosingBrowser(false);
      fetchStatus();
    }
  };

  if (adminLoading || (!isPlatformAdmin && !adminLoading)) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            {adminLoading ? (
              <>
                <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
                <p>Yetki kontrol ediliyor...</p>
              </>
            ) : (
              <>
                <AlertTriangle className="w-8 h-8 text-destructive mx-auto mb-4" />
                <p>Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
              </>
            )}
          </div>
        </div>
      </Layout>
    );
  }

  const formatTimestamp = (ts: string | null) => {
    if (!ts) return '-';
    return new Date(ts).toLocaleString('tr-TR');
  };

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Shield className="w-8 h-8 text-yellow-500" />
              Mars Fiyat Kazıyıcı
            </h1>
            <p className="text-muted-foreground">
              mars.arcelik.com.tr portalından fiyat kazıma
            </p>
          </div>
          <Button onClick={fetchStatus} variant="outline" size="icon">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Service Error Alert */}
        {serviceError && (
          <Card className="border-destructive bg-destructive/10">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-destructive">Servis Bağlantı Hatası</p>
                  <p className="text-sm text-muted-foreground mt-1">{serviceError}</p>
                  <code className="block mt-2 text-xs bg-muted p-2 rounded">
                    cd scraper-service && npm run dev
                  </code>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Step 1: Open Browser */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
                  1
                </div>
                Tarayıcı Aç (Giriş + 2FA)
              </CardTitle>
              <CardDescription>
                Mars portalına giriş yapmak için tarayıcı açın ve manuel olarak 2FA doğrulamasını tamamlayın.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button 
                onClick={handleOpenBrowser}
                disabled={openingBrowser || !status || serviceError !== null}
                className="w-full h-16 text-lg"
                size="lg"
              >
                {openingBrowser ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    Açılıyor...
                  </>
                ) : (
                  <>
                    <Globe className="w-5 h-5 mr-2" />
                    mars.arcelik.com.tr Aç
                  </>
                )}
              </Button>
              
              {status?.browserOpen && (
                <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-950 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-700 dark:text-green-300">Tarayıcı açık</span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleCloseBrowser}
                    disabled={closingBrowser}
                  >
                    {closingBrowser ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <X className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Step 2: Scrape */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
                  2
                </div>
                Otomatik Kazıma Başlat
              </CardTitle>
              <CardDescription>
                {CATEGORY_URLS.length} önceden tanımlanmış kategori otomatik olarak kazınacak.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button 
                onClick={handleStartScrape}
                disabled={scraping || !status?.browserOpen || serviceError !== null}
                className="w-full h-16 text-lg"
                size="lg"
                variant={status?.browserOpen ? "default" : "secondary"}
              >
                {scraping ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    {scrapingProgress 
                      ? `Kazınıyor... (${scrapingProgress.currentIndex}/${scrapingProgress.total})`
                      : 'Kazınıyor...'
                    }
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5 mr-2" />
                    Kazımayı Başlat ({CATEGORY_URLS.length} Kategori)
                  </>
                )}
              </Button>
              
              {!status?.browserOpen && status && (
                <p className="text-sm text-muted-foreground text-center">
                  Önce tarayıcıyı açın ve giriş yapın
                </p>
              )}

              {scrapingProgress && (
                <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg text-sm space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-blue-700 dark:text-blue-300 font-medium">
                      İlerleme: {scrapingProgress.currentIndex}/{scrapingProgress.total}
                    </span>
                    <span className="text-blue-600 dark:text-blue-400 text-xs">
                      {Math.round((scrapingProgress.currentIndex / scrapingProgress.total) * 100)}%
                    </span>
                  </div>
                  {scrapingProgress.currentUrl && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 truncate">
                      Şu an: {scrapingProgress.currentUrl}
                    </p>
                  )}
                  <div className="w-full bg-blue-200 dark:bg-blue-900 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${(scrapingProgress.currentIndex / scrapingProgress.total) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {status?.browserOpen && !scraping && CATEGORY_URLS.length > 0 && (
                <div className="p-3 bg-muted rounded-lg text-sm">
                  <p className="font-medium mb-2">Kazınacak Kategoriler:</p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    {CATEGORY_URLS.slice(0, 3).map((url, i) => (
                      <li key={i} className="truncate">• {url.split('/').pop()?.split('.')[0].replace(/%20/g, ' ')}</li>
                    ))}
                    {CATEGORY_URLS.length > 3 && (
                      <li className="text-primary">+ {CATEGORY_URLS.length - 3} daha...</li>
                    )}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Session Panel - Show when session is active */}
        {status?.session?.active && (
          <Card className="border-primary/50 bg-primary/5">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-primary" />
                Aktif Oturum
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                <div className="text-center p-3 bg-background rounded-lg">
                  <div className="text-2xl font-bold text-primary">{status.session.categoriesScraped}</div>
                  <div className="text-xs text-muted-foreground">Kategori Kazındı</div>
                </div>
                <div className="text-center p-3 bg-background rounded-lg">
                  <div className="text-2xl font-bold">{status.session.totalProductsScraped}</div>
                  <div className="text-xs text-muted-foreground">Toplam Ürün</div>
                </div>
                <div className="text-center p-3 bg-green-50 dark:bg-green-950 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{status.session.totalProductsInserted}</div>
                  <div className="text-xs text-muted-foreground">Yeni Eklenen</div>
                </div>
                <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-950 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">{status.session.totalSkippedToday}</div>
                  <div className="text-xs text-muted-foreground">Atlanan (Bugün Var)</div>
                </div>
                <div className="flex items-center justify-center">
                  <Button 
                    onClick={handleFinishSession}
                    disabled={finishingSession}
                    variant="outline"
                    className="w-full"
                  >
                    {finishingSession ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Bitiriliyor...
                      </>
                    ) : (
                      <>
                        <Flag className="w-4 h-4 mr-2" />
                        Oturumu Bitir
                      </>
                    )}
                  </Button>
                </div>
              </div>
              {status.session.startedAt && (
                <p className="text-xs text-muted-foreground text-center">
                  Oturum başlangıcı: {new Date(status.session.startedAt).toLocaleString('tr-TR')}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Status Panel */}
        {status && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Status Cards */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Tarayıcı Durumu</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  {status.browserOpen ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-medium">Açık</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-5 h-5 text-muted-foreground" />
                      <span className="text-muted-foreground">Kapalı</span>
                    </>
                  )}
                </div>
                {status.browserOpen && (
                  <div className="mt-2 flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Giriş durumu:</span>
                    <Badge variant={status.loggedInHeuristic ? "default" : "secondary"}>
                      {status.loggedInHeuristic ? 'Muhtemelen giriş yapılmış' : 'Bilinmiyor'}
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Son Çalıştırma</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span>{formatTimestamp(status.lastRun)}</span>
                </div>
                {status.lastRunResult && (
                  <div className="mt-2">
                    {status.lastRunResult.ok ? (
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span className="text-green-700 dark:text-green-400">Başarılı</span>
                        </div>
                        <div className="text-muted-foreground">
                          {status.lastRunResult.validProducts} ürün, {status.lastRunResult.durationSeconds}s
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm">
                        <XCircle className="w-4 h-4 text-destructive" />
                        <span className="text-destructive">{status.lastRunResult.error}</span>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Yapılandırma</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Supabase:</span>
                  <Badge variant={status.config.supabaseConfigured ? "default" : "destructive"}>
                    {status.config.supabaseConfigured ? 'Yapılandırıldı' : 'Yapılandırılmadı'}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground truncate" title={status.config.marsCategoryUrl}>
                  Kategori: {status.config.marsCategoryUrl}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Logs */}
        {status && status.logs.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Terminal className="w-5 h-5" />
                Loglar
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-64 w-full rounded border bg-muted/50 p-4 font-mono text-xs">
                {status.logs.slice().reverse().map((log, index) => (
                  <div 
                    key={index} 
                    className={`py-0.5 ${
                      log.level === 'error' ? 'text-destructive' : 
                      log.level === 'warn' ? 'text-yellow-600 dark:text-yellow-400' : 
                      'text-muted-foreground'
                    }`}
                  >
                    <span className="text-muted-foreground/60">
                      [{new Date(log.timestamp).toLocaleTimeString('tr-TR')}]
                    </span>
                    {' '}
                    <span className={`font-medium uppercase ${
                      log.level === 'error' ? 'text-destructive' : 
                      log.level === 'warn' ? 'text-yellow-600' : 
                      'text-blue-600 dark:text-blue-400'
                    }`}>
                      [{log.level}]
                    </span>
                    {' '}
                    {log.message}
                  </div>
                ))}
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Last Run Results */}
        {status?.lastRunResult?.ok && (
          <Card>
            <CardHeader>
              <CardTitle>Son Kategori Kazıma Sonuçları</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{status.lastRunResult.totalExtracted}</div>
                  <div className="text-sm text-muted-foreground">Toplam Bulunan</div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{status.lastRunResult.validProducts}</div>
                  <div className="text-sm text-muted-foreground">Geçerli Ürün</div>
                </div>
                <div className="text-center p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                  <div className="text-2xl font-bold text-green-700 dark:text-green-400">
                    {status.lastRunResult.newProductsInserted || 0}
                  </div>
                  <div className="text-sm text-muted-foreground flex items-center justify-center gap-1">
                    <Database className="w-3 h-3" />
                    Yeni Eklenen
                  </div>
                </div>
                <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-950 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-700 dark:text-yellow-400">
                    {status.lastRunResult.skippedAlreadyToday || 0}
                  </div>
                  <div className="text-sm text-muted-foreground flex items-center justify-center gap-1">
                    <SkipForward className="w-3 h-3" />
                    Atlanan (Bugün)
                  </div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{status.lastRunResult.errorsCount}</div>
                  <div className="text-sm text-muted-foreground">Hatalı</div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{status.lastRunResult.clicksToLoadAll}</div>
                  <div className="text-sm text-muted-foreground">Sayfa Yükleme</div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{status.lastRunResult.durationSeconds}s</div>
                  <div className="text-sm text-muted-foreground">Süre</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Instructions */}
        <Card>
          <CardHeader>
            <CardTitle>Kullanım Talimatları</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <h4 className="font-medium">1. Scraper servisini başlatın</h4>
              <code className="block bg-muted p-3 rounded text-sm">
                cd scraper-service && npm run dev
              </code>
            </div>
            <Separator />
            <div className="space-y-2">
              <h4 className="font-medium">2. Tarayıcıyı açın</h4>
              <p className="text-sm text-muted-foreground">
                "mars.arcelik.com.tr Aç" butonuna tıklayın. Bir Chromium penceresi açılacaktır.
              </p>
            </div>
            <Separator />
            <div className="space-y-2">
              <h4 className="font-medium">3. Manuel giriş yapın</h4>
              <p className="text-sm text-muted-foreground">
                Açılan tarayıcı penceresinde Mars portalına giriş yapın ve 2FA doğrulamasını tamamlayın.
              </p>
            </div>
            <Separator />
            <div className="space-y-2">
              <h4 className="font-medium">4. Otomatik kategori kazıma</h4>
              <p className="text-sm text-muted-foreground">
                <strong>a)</strong> "Kazımayı Başlat" butonuna tıklayın.<br />
                <strong>b)</strong> Sistem önceden tanımlanmış {CATEGORY_URLS.length} kategoriyi otomatik olarak sırayla kazıyacaktır.<br />
                <strong>c)</strong> Her kategori için ilerleme çubuğunda durumu görebilirsiniz.<br />
                <strong>d)</strong> Tüm kategoriler tamamlandığında özet bildirim alacaksınız.
              </p>
            </div>
            <Separator />
            <div className="space-y-2">
              <h4 className="font-medium">Önemli Bilgiler</h4>
              <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                <li><strong>Aynı gün, aynı ürün:</strong> Zaten bugün kaydedilmiş ürünler atlanır (güncellenmez).</li>
                <li><strong>Farklı gün, aynı ürün:</strong> Yeni satır olarak eklenir (geçmiş veriler korunur).</li>
                <li><strong>Fiyat geçmişi:</strong> Her gün için ayrı kayıt tutulur, böylece fiyat değişimlerini takip edebilirsiniz.</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminMarsScraper;
